import API from "api/API"
import {LanguageFileLocations} from "app/Service/System/Language/LanguageFileLocations"
import i18n from "i18next"
import LanguageDetector from 'i18next-browser-languagedetector'
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {initReactI18next} from "react-i18next"
import EnvironmentService, {subscribeEnv} from "app/Service/System/EnvironmentService"
import {envStore} from "store/system/environment/env.store"
import getBrowserData from "utils/Analytics/window.navigator"
import {isDev} from "utils/isDev"

function LanguageService(caller: string) {

  const {language, setLanguage} = EnvironmentService([
    subscribeEnv.language
  ], 'LanguageService')

  const [isLoading, setIsLoading] = useState<boolean>()

  useEffect(() => {
    Logger(`Service.Language.init.${caller}`)

    return () => {
      Logger(`Service.Language.unloading.${caller}`)
    }
  }, [])

  useEffect(() => {
    if (undefined === isLoading) return
    envStore.update(state => {
      state.languageLoading = isLoading
    })
  }, [isLoading])

  useEffect(function initI18n() {
    (async () => {
      // Register languageChanged event to catch the 'i18next-browser-languageDetector' setting the language
      i18n.on('languageChanged', language => languageChanged(language))

      const languageDetector = new LanguageDetector(null, {
        order: ['navigator', 'querystring'],
        lookupQuerystring: 'lng',
        caches: []
      })

      await i18n
      .use(languageDetector)
      .use(initReactI18next)
      .init({
        debug: isDev(),
        // We need to provide an empty resources object. we will load / add onto this later.
        resources: {},
        interpolation: {
          escapeValue: false  // React already saves from XSS
        },
        // By disabling this flag, we allow the detector to send anything it 'detects' and we can log this through the API
        //  Otherwise the detector will limit itself to the given options.
        // supportedLngs: ['nl', 'nld', 'de', 'en', 'eng'],
        nonExplicitSupportedLngs: true,
        // https://www.i18next.com/overview/configuration-options#initimmediate
        // Basically suppresses warning message that no languages are loaded.
        // This is, because we load the languages below on language change effect
        // initImmediate: false,
        fallbackLng: false,
        react: {
          // bindI18n: 'languageChanged',
          transEmptyNodeValue: 'translation-error',
          transSupportBasicHtmlNodes: true,
          transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
          useSuspense: true,
        }
      })
    })()
  }, [])

  // If ENV language is undefined, we let i18n set the language from the detected languages
  // If we don't grab the value from the store directly, it will fire twice.
  // - First because no language is set.
  // - Second time because this method has set a language, but the store push has not come through
  // - Third time because the store value comes through, so its now defined and will not run anymore.
  //  - Not 100% this is the best solution. Maybe some react hook can solve the issue while working with store values and i18n onChange event handler.
  function languageChanged(lng: string | undefined) {
    if (undefined === lng) return
    // Grab each time so that we don't have to wait for the subscribe action to update
    if (undefined === envStore.getValue().language) {
      const d = {navigator: {...getBrowserData()}, language: lng}
      const ds = JSON.stringify(d)
      const data = btoa(ds)
      API.put('s/language',  {seekAndDestroy: data}).then()
      switch (lng) {
        case "nl" :
        case "nld":
          return setLanguage('nld')
        case "en-US":
        case "eng":
          return setLanguage('eng')
        default:
          return setLanguage('eng')
      }
    }
  }

  // Take the fileLocations array and wait for all language files to be loaded.
  // Once done, loaded flag gets set to false.
  useEffect(function loadNewLanguage() {
    if (undefined === language) return
    setIsLoading(true)

    let isEffectStale = false

    const i18nLanguage = language
    const keys = Object.keys(LanguageFileLocations) as string[]

    Promise.all(keys.map(key => {
      if (isEffectStale) return
      const filesNames = Object.values(LanguageFileLocations[key])
      Promise.all(filesNames.map(fileName => {
        if (isEffectStale) return
        const file = require('../../../../locale/' + language + '/' + key + '/' + fileName)
        i18n.addResourceBundle(i18nLanguage, `${key}.${fileName}`, file.default, false, false)
      })).then()
    })).then(() => {
      setIsLoading(false)
    })

    return () => {
      isEffectStale = true
    }
  }, [language])

  // Update the language in i18n if the new language is different.
  useEffect(() => {
    if (language && false === isLoading)
      i18n.changeLanguage(language).then()
  }, [isLoading])

  return {
    isLoadingLanguage: isLoading,
  }
}

export default LanguageService
